import ErrorBoundary from '@/components/ErrorBoundary';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { EmailTemplatePreviewModal } from '@/features/campaigns/EmailCampaign/templates/EmailTemplatePreviewModal';
import { HtmlEditorPortal } from '@/features/campaigns/EmailCampaign/templates/HtmlEditorPortal';
import type { EmailTemplateFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Email/TemplatePreview/EmailTemplatePreview';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { hasMappingsUi } from '@/features/connectors/flows';
import { useEmailConnectorFlowRunner } from '@/features/connectors/email';
import { useTemplateImageUpload } from '@/features/campaigns/hooks/useTemplateImageUpload';
import { RouteConstants } from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { ToastType } from '@/models/toast';
import HtmlEditor from '@/modules/HTML_EDITOR';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import { Button } from '@/lib';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type CreationPhase = 'metadata' | 'design' | 'mappings';

const EMAIL_IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/gif'];

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

export const EmailTemplateCreation = () => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();
  const { upload: uploadTemplateImage } = useTemplateImageUpload();

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    createInstance,
    isCreating,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useEmailConnectorFlowRunner('templateInsert', {
    audienceId,
    lang: currentLanguage,
  });

  const [phase, setPhase] = useState<CreationPhase>('metadata');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [bodyHtml, setBodyHtml] = useState('');
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<EmailTemplateFormData | null>(null);

  const showMappingsStep = hasMappingsUi(multiStepMappingsConfig);

  const buildFlowParams = useCallback(
    (overrides?: FlowFormData): FlowFormData => ({
      ...formData,
      ...overrides,
      template_body_html: overrides?.['template_body_html'] ?? bodyHtml,
    }),
    [formData, bodyHtml]
  );

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = {
      ...(opts?.flowParams ?? buildFlowParams()),
      template_body_html: (opts?.flowParams?.['template_body_html'] as string | undefined) ?? bodyHtml,
    };
    const flowMappings = opts?.flowMappings ?? mappingData;

    if (!String(flowParams['template_body_html'] ?? '').trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      setPhase('design');
      return { success: false, message: 'Missing template body' };
    }

    if (!opts?.fromFlowComplete && hasMappingsUi(multiStepMappingsConfig) && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    if (!connectorInstanceId) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const result = await createInstance(flowParams, flowMappings);
    if (!result.success) {
      const errorMessage = result.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
      return { success: false, message: errorMessage };
    }

    appDispatch(showToast({ title: 'Template created successfully', type: ToastType.SUCCESS }));
    appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
    history.push(RouteConstants.emailTemplates);
    return { success: true };
  };

  const handleEditorSave = ({ name, html }: { name: string; html: string }) => {
    if (!html.trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      return;
    }

    const nextFormData = { ...formData, template_name: name };
    setFormData(nextFormData);
    setBodyHtml(html);

    if (hasMappingsUi(multiStepMappingsConfig)) {
      setPhase('mappings');
      return;
    }

    void handleCreateTemplate({
      flowParams: { ...nextFormData, template_body_html: html },
      flowMappings: {},
      fromFlowComplete: true,
    });
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.emailTemplates),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () =>
      void handleCreateTemplate({
        flowParams: buildFlowParams(),
        flowMappings: mappingData,
      }),
    disabled: !isMappingsFormValid || isCreating,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFlowComplete = (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
    setFormData(allParams);
    setMultiStepMappingsConfig(mappingsConfig);
    setMappingData({});
    setIsMappingsFormValid(false);
    setBodyHtml(typeof allParams['template_body_html'] === 'string' ? allParams['template_body_html'] : '');
    setPhase('design');
  };

  const isTemplateLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);

  if (phase === 'design') {
    return (
      <>
        <HtmlEditorPortal>
          <HtmlEditor
            templateName={String(formData['template_name'] ?? '')}
            value={bodyHtml}
            onChange={setBodyHtml}
            onTemplateNameChange={(name) => setFormData((prev) => ({ ...prev, template_name: name }))}
            onClose={() => setPhase('metadata')}
            onPreview={(html) =>
              setPreviewFormData({
                ...(formData as EmailTemplateFormData),
                template_body_html: html,
              })
            }
            onSave={handleEditorSave}
            onUploadImage={(file) => uploadTemplateImage(file, EMAIL_IMAGE_ACCEPT)}
          />
        </HtmlEditorPortal>
        {previewFormData ? <EmailTemplatePreviewModal formData={previewFormData} onClose={() => setPreviewFormData(null)} /> : null}
      </>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('emailWorkspace.templates.createEmailTemplate')}</h2>

      {isAudienceLoading ? (
        <p className='text-sm text-gray-600'>{t('emailWorkspace.templates.templateLoadingMessage')}</p>
      ) : isAudienceSettled && !hasAudience ? (
        <AudienceRequiredEmptyState />
      ) : (
        <div className={phase === 'metadata' ? undefined : 'hidden'}>
          <ErrorBoundary>
            {prepareResult ? (
              <MultiStepFlowForm
                key={`${connectorFlowId}-${audienceId}`}
                id='email-template-create-form'
                showTemplatePreview
                templatePreviewVariant='email'
                htmlBodyFieldMode='external'
                lastStepActionLabel={t('emailWorkspace.templates.continueToEmailDesigner')}
                audienceId={audienceId!}
                connectorInstanceId={connectorInstanceId!}
                connectorFlowId={connectorFlowId!}
                paramsConfig={prepareResult}
                onComplete={handleFlowComplete}
              />
            ) : (
              <div className='flex flex-col items-start gap-3'>
                <p className='text-sm text-gray-600'>
                  {isTemplateLoading && !prepareError
                    ? t('emailWorkspace.templates.templateLoadingMessage')
                    : t('emailWorkspace.templates.failedToLoadTemplate')}
                </p>
                {prepareError ? (
                  <Button intent='secondary' onClick={() => reloadPrepare()} text={t('emailWorkspace.templates.tryAgain')} />
                ) : null}
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {phase === 'mappings' && showMappingsStep ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
